import { 
    SimplifiedTraceEvent, Shipment, ProductMeta, ProductionDate, 
    RoutingDecision, LeadTimeBreakdown, RawLeadTime, PackageData, 
    ReceivedDeliveryOption, ScheduledLeadTime, DeliveryOption 
  } from '../models/DeliveryOptionDetailsModel';
  
  export class TraceDataProcessor {
    constructor(
      private debugJson: SimplifiedTraceEvent[],
      private catalogLookups: Record<string, any> | null
    ) {}
  
    public process() {
      if (!this.debugJson) return null;
  
      // --- EXACT ORIGINAL VARIABLES ---
      const meta: Record<string, ProductMeta> = {};
      const carrierIdsFromTrace: Record<string, string> = {};
      const carrierNetworksFromTrace: Record<string, string> = {};
      const prodCalendar: Record<string, ProductionDate[]> = {};
      const productShipDates: Record<string, string> = {};    
      const routingDecisions: Record<string, RoutingDecision> = {};
      const detailedLeadTimes: Record<string, LeadTimeBreakdown> = {};
      const rawLeadTimeTrace: Record<string, RawLeadTime> = {};
      const preBundleData: Record<string, PackageData> = {};
      const optionsByActivityId: Record<string, ReceivedDeliveryOption[]> = {};
      const activityIdToPlant: Record<string, string> = {};    
      const selectedCentiroOptions: Record<string, ReceivedDeliveryOption[]> = {};
      const availableSchedules: Record<string, ScheduledLeadTime[]> = {};
  
      let orderDate: string | null = null;
      let shipments: Shipment[] = [];
  
      const activityToContext: Record<string, { art: string, site: string }> = {};
  
      // --- EXACT ORIGINAL HELPER ---
      const findDescriptionByArticleCode = (articleCode: string): string | null => {
        if (!this.catalogLookups) return null;
        const productCodeLookups = (this.catalogLookups as any).filter(
          (lookup: any) => lookup.lookupType === "productCode"
        );
        for (const productCodeLookup of productCodeLookups) {
          const child = productCodeLookup.children.find(
            (child: any) => child.key.toLowerCase() === articleCode.toLowerCase()
          );
          if (child) return child.description;
        }
        return null;
      };
  
      // --- FIRST PASS ---
      this.debugJson.forEach(event => {
        const activityId = event.fullTraceEvent?.activityId;
        if (event.name === "LeadTime" && event.data?.ArticleCode) {
          const art = event.data.ArticleCode;
          const site = event.data.PlantCode || "Global";
          const compoundKey = `${art}-${site}`;
          rawLeadTimeTrace[compoundKey] = event.data;
          const parentActivityId = event.fullTraceEvent?.parentActivityId;
          if (parentActivityId) activityToContext[parentActivityId] = { art, site };
        }
  
        if (event.name === "Available-Carriers-Request" && activityId) {
          activityIdToPlant[activityId] = event.data?.PlantCode;
        }
        if (event.name === "Received-Delivery-Options" && activityId) {
          optionsByActivityId[activityId] = event.data || [];
        }
        if (event.name === "Delivery-Option-Selected" && activityId) {
          selectedCentiroOptions[activityId] = event.data;        
        }
        if (event.name === "Available-Schedules" && activityId) {
          event.data?.forEach((schedule: any) => {availableSchedules[schedule.Id] = schedule});        
        }
      });
  
      // --- SECOND PASS ---
      this.debugJson.forEach(event => {
        if (event.source === "DeliveryOptions-Api" && event.name === "Request-Started") orderDate = event.data?.OrderDate?.split('T')[0] || null;
        if (event.name === "Product-Ship-Date") productShipDates[event.data.Products] = event.data.ShipDate?.split('T')[0];
        if (event.name === "Plant-Selected") {
          event.data.Products?.forEach((p: any) => { 
            meta[p.Tag] = p; 
            meta[p.Tag].Description = findDescriptionByArticleCode(p.ArticleCode) || p.ArticleCode;
          });        
          event.data.ProductRoutingDecision?.forEach((rd: any) => { routingDecisions[rd.Tag] = rd; });
        }
        if (event.name === "Detailed-LeadTime") {
          const activityId = event.fullTraceEvent?.activityId;
          const context = activityId ? activityToContext[activityId] : null;
          if (context) detailedLeadTimes[`${context.art}-${context.site}`] = event.data;
        }
        if (event.name === "Calculated-Pre-Bundles") {
          event.data?.forEach((bundle: any) => {
            bundle.Products?.forEach((prod: any) => { preBundleData[prod.Tag] = prod; });
          });
        }
        if (event.name === "Production-Dates") {
          const productIds = Array.isArray(event.data.Products) ? event.data.Products : [event.data.Products];
          productIds.forEach((pid: string) => { if (event.data.Dates) prodCalendar[pid] = event.data.Dates; });
        }
  
        // SCRAPING LOGIC
        if (event.source === "DeliveryOptions-ShipmentCalculator" && event.name === "Request-Finished") {
          const scShipments = event.data?.Result?.DraftOrderCalculations?.[0]?.Shipments || [];
          scShipments.forEach((sc: any) => {
            shipments.push({
              PlantCode: sc.PlantCode,
              Products: sc.Products,
              DeliveryOptions: [], 
              ShipDate: null,
              PackageGeometry: sc.Package ? {
                PackageName: sc.Package.PackageName,
                Shape: sc.Package.Shape,
                LengthMm: sc.Package.LengthInMm,
                BreadthMm: sc.Package.WidthInMm,
                HeightMm: sc.Package.HeightInMm,
                WeightGrams: sc.Package.WeightInGrams
              } : undefined
            });
          });
        }
  
        if (event.source === "DeliveryOptions-Api" && event.name === "Request-Finished") {
          const apiShipments = event.data?.Result?.Shipments || [];
          shipments.forEach(s => {
            const match = apiShipments.find((as: any) => as.PlantCode === s.PlantCode && as.Products[0]?.Tag === s.Products[0]?.Tag);
            if (match) s.DeliveryOptions = match.DeliveryOptions;
          });
        }
  
        if (event.name === "Available-Carriers-Response" || event.name === "Delivery-Option-Selected") {
          const items = Array.isArray(event.data) ? event.data : [event.data];
          items.forEach((opt: any) => {
            if (opt.CarrierName) {
              carrierIdsFromTrace[opt.CarrierName] = opt.CarrierServiceId;
              carrierNetworksFromTrace[opt.CarrierName] = opt.NetworkId;
            }
          });
        }
      });
  
      // --- GROUPING AND ENRICHMENT ---
      const groupedSites: Record<string, Shipment[]> = {};
      shipments.forEach(s => {
        if (!groupedSites[s.PlantCode]) {
          groupedSites[s.PlantCode] = []; 
        }
        groupedSites[s.PlantCode].push(s);
        s.ShipDate = productShipDates[s.Products[0]?.Tag];
        const matchingActivityId = Object.keys(activityIdToPlant).find(aid => activityIdToPlant[aid] === s.PlantCode);
  
        s.DeliveryOptions.forEach((deliveryOption: DeliveryOption) => {
        const filteredReceivedOptions = (matchingActivityId ? optionsByActivityId[matchingActivityId] : [])
          .filter((rcv: ReceivedDeliveryOption) => 
            rcv.DeliveryMethod.toLowerCase() === deliveryOption.ShippingDetails.DeliveryMethod.toLowerCase() &&
            rcv.CarrierServiceType.toLowerCase() === deliveryOption.ShippingDetails.DeliveryType.toLowerCase()
          );      
          deliveryOption.CentiroOptions = filteredReceivedOptions;
          deliveryOption.SelectedCentiroOption = (selectedCentiroOptions[matchingActivityId!] as any)?.find((x:ReceivedDeliveryOption) => x.DeliveryMethod === deliveryOption.ShippingDetails.DeliveryMethod && x.CarrierServiceType === deliveryOption.ShippingDetails.DeliveryType);        
        });            
        s.Products.forEach(p => {
          p.ProductMeta = meta[p.Tag];
          p.PackageData = preBundleData[p.Tag];
          const compoundKey = `${p.ProductMeta?.ArticleCode}-${s.PlantCode}`;        
          p.ProductLeadTime =  {LeadTimeBreakdown: detailedLeadTimes[compoundKey], RawLeadTime: rawLeadTimeTrace[compoundKey]}
          p.ProductLeadTime?.RawLeadTime?.ScheduledLeadTimes?.forEach((schedule: ScheduledLeadTime) => {
            schedule.Available = (availableSchedules as any)[schedule.Id] != null || false;
          });
        });
      });
  
      return { 
        sites: groupedSites, meta, carrierIdsFromTrace, carrierNetworksFromTrace, 
        prodCalendar, orderDate, productShipDates, routingDecisions, 
        detailedLeadTimes, rawLeadTimeTrace, preBundleData, 
        optionsByActivityId, activityIdToPlant
      };
    }
  }